import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useABTestExperimentsContext } from '@tn/abtests/providers/ABTestExperimentsProvider';
import { getCookie } from 'cookies-next';
import { useRouter } from 'next/router';

import { useFeatureFlags } from '@/entities/feature-flags/hooks/useFeatureFlags';
import { TrackingCategory, TrackingType } from '@/entities/tracking/const';
import { BootstrapSignalKeys, BootstrapSignals } from '@/entities/tracking/lib/bootstrap-signals';
import { TrackerEngine } from '@/entities/tracking/lib/tracker-engine';
import { getPageType, usePageTypeStore } from '@/entities/tracking/stores/pageTypeStore';
import { FullUser } from '@/graphql/__generated/resolvers-types';
import useAppProvider from '@/hooks/providers/useAppProvider';
import useFullUser from '@/hooks/providers/useFullUser';
import useRegion from '@/hooks/providers/useRegion';
import useTrackEvent from '@/hooks/tracking/useTrackEvent';
import { useTrackingSiteName } from '@/hooks/tracking/useTrackingSiteName';
import useEventListeners from '@/hooks/utils/useEventListeners';
import { appLogger } from '@/utils/app-logger/appLogger';
import {
    DEV,
    EMPTY_STR,
    NA,
    OTHER,
    SITE_CODE,
    SITE_ID,
    SITE_PRIMARY_RSID,
    SITE_TYPE,
    USER_STATUS_COOKIE_KEY,
} from '@/utils/constants/common';
import {
    ROUTE_CHANGE_START,
    UPDATE_DATA_LAYER,
    USER_STATUS,
    USER_TYPE,
} from '@/utils/constants/tracking';
import { USER_STATUS as API_USER_STATUS } from '@/utils/constants/userStatus';
import { createOptimizelyExpString } from '@/utils/createOptimizelyExpString';
import { toError } from '@/utils/errors/toError';
import generateUUID from '@/utils/generateUUID';
import { getOSName } from '@/utils/getOSName';
import {
    buildAdobeUserTrackingFields,
    buildClearedSubscriberAdobeDataLayerFields,
    buildClearedSubscriberFlatAdobeFields,
    clearSubscriberUtagFields,
    hasSubscriberAdobeDataLayerState,
} from '@/utils/tracking/buildAdobeUserTrackingFields';
import {
    fireProfilePickerPageLoadBeacon,
    shouldFirePickerBeaconOnPageTypeResolve,
    shouldFirePickerBeaconOnSubscriberSyncKeyChange,
} from '@/utils/tracking/fireProfilePickerPageLoadBeacon';
import { getBrowserNameVersion } from '@/utils/tracking/getBrowserNameVersion';
import { getTrackingBrandPlatform } from '@/utils/tracking/getTrackingBrandPlatform';
import { getUserInfo } from '@/utils/tracking/getUserInfo';
import {
    getSubscriberUtagSyncKey,
    isProfilePickerRoute,
    isUserDataReadyForPageView,
    shouldEmitAnonUserInfo,
    shouldEmitSubscriberTrackingFields,
} from '@/utils/tracking/isUserDataReadyForPageView';
import { updateOptimizelyExp } from '@/utils/tracking/updateOptimizelyExp';

import { getSiteEdition } from '../../components/DataLayer/DataLayer';
import { DataLayerFunctions } from '../../components/DataLayer/DataLayerTypes';

type AdobeDataLayerStateSnapshot = {
    pageInfo?: { pageType?: string };
    userInfo?: Record<string, unknown>;
};

type AppBaseSnapshotResult = {
    /** False when the user query was still loading, so nothing reached the data layer. */
    pushed: boolean;
    /** The pageType the snapshot was built with, for page-type-sensitive follow-ups. */
    pageType: string;
};

export type AppOperation = {
    siteInfo: SiteInfo;
};

export type SiteInfo = {
    brandPlatformId: string;
    siteCode: string;
    siteEdition: string;
    siteHierPrefix: string;
    siteId: string;
    sitePrimaryRsid: string;
    siteTypeDevice: string;
    optimizelyExp: string;
    autoPlayEnabled: boolean;
    genre: string;
    contentBrand: string;
};

const CONTINUOUS_PLAY = 'continuous-play';
const PICKER_BEACON_RETRY_INTERVAL_MS = 100;
const PICKER_BEACON_RETRY_TIMEOUT_MS = 5000;
// '\0' prefix guarantees no real sync key (which uses '|'-delimited printable values) ever collides.
const SUBSCRIBER_UTAG_SYNCED_ON_REMOUNT = '\0remount';
const hasLoggedInStatusCookie = () => {
    const status = getCookie(USER_STATUS_COOKIE_KEY);

    return Boolean(status && status !== API_USER_STATUS.ANONYMOUS);
};

export const useTrackAppBaseSnapshot = (): void => {
    const router = useRouter();
    const { getFeatureFlag } = useFeatureFlags();
    const { region, locale } = useRegion();
    const { siteName, isReady: isSiteNameReady } = useTrackingSiteName();
    const { brandPlatform, siteTypeDevice } = getTrackingBrandPlatform({ siteName });
    const {
        userFullProfile,
        isLoading: isUserDataLoading,
        isFetched: isUserQueryFetched,
        isError: isUserQueryError,
    } = useFullUser();
    const hasLoggedInSession = hasLoggedInStatusCookie();
    const { env } = useAppProvider();
    const trackEvent = useTrackEvent();
    // Subscribed so the effects below re-run; `emitAppBaseSnapshot` reads at call time instead.
    const pageType = usePageTypeStore((state) => state.pageType);
    const experimentsContext = useABTestExperimentsContext();
    const experimentsListForTracking = useMemo(
        () => ({ ...experimentsContext.state?.experimentsListForTracking }),
        [experimentsContext.state?.experimentsListForTracking],
    );
    const infoUpdateAlreadyMarked = BootstrapSignals.has(BootstrapSignalKeys.INFO_UPDATE);
    const hasBootstrappedRef = useRef(infoUpdateAlreadyMarked);
    const bootstrapCompletedRef = useRef(infoUpdateAlreadyMarked);
    const lastSyncedSubscriberKeyRef = useRef<string | null>(
        infoUpdateAlreadyMarked ? SUBSCRIBER_UTAG_SYNCED_ON_REMOUNT : null,
    );
    const lastFiredPageViewSyncKeyRef = useRef<string | null>(null);
    const pickerBeaconAttemptedSyncKeyRef = useRef<string | null>(null);
    /** A nav-driven INFO_UPDATE mark waiting for the user query to settle (see updateDataLayer). */
    const pendingNavInfoUpdateMarkRef = useRef(false);
    /**
     * Bumped on each MFE routeChangeStart, alongside useTrackNavigation's INFO_UPDATE revoke.
     * Nav-driven re-marks capture it before awaiting the snapshot and bail if it moved: a mark
     * that lands after a newer navigation's revoke would release PAGE_VIEW and the AOP
     * navigation hold against the outgoing page's context (PTVWS-6991).
     */
    const navGenerationRef = useRef(0);
    const lastEmittedExperimentsRef = useRef(experimentsContext.state?.experimentsListForTracking);
    // Mirrors hasBootstrappedRef's flip as a render-triggering value, so a settle the XHR
    // effect skipped while bootstrap was still in flight gets re-evaluated once it flips.
    const [bootstrapReadyTick, setBootstrapReadyTick] = useState(0);

    const autoPlayEnabled = getFeatureFlag(CONTINUOUS_PLAY) === true ? '1' : '0';

    const { location, screen, viewport, navigator, documentObject } = useMemo(() => {
        let serverWindow: typeof window | undefined;
        let serverDocument: typeof document | undefined;
        if (typeof window !== 'undefined') serverWindow = window;
        if (typeof document !== 'undefined') serverDocument = document;

        return {
            location: serverWindow?.location,
            screen: serverWindow?.screen,
            navigator: serverWindow?.navigator,
            documentObject: serverDocument,
            pageType,
            viewport: {
                height: serverWindow?.innerHeight ?? 0,
                width: serverWindow?.innerWidth ?? 0,
            },
        };
    }, [pageType]);

    const getDomInfo: DataLayerFunctions['getDomInfo'] = useCallback(
        () => ({
            pathname: location?.pathname,
            hash: location?.hash,
            domain: location?.hostname,
            query_string: location?.search,
            userAgent: navigator?.userAgent,
            referrer: documentObject?.referrer,
            viewport,
        }),
        [
            documentObject?.referrer,
            location?.hash,
            location?.hostname,
            location?.pathname,
            location?.search,
            navigator?.userAgent,
            viewport,
        ],
    );

    // Deprecated, but UA emulation never spoofs it: the real-OS signal getOSName relies on.
    const navigatorPlatform = navigator?.platform; // NOSONAR

    const getAppInfo: DataLayerFunctions['getAppInfo'] = useCallback(() => {
        const { browserName, browserVersionNumber } = getBrowserNameVersion(navigator?.userAgent);
        return {
            countryCode: region,
            userAgent: navigator?.userAgent,
            browserName,
            browserVersion: browserVersionNumber,
            clientRegion: userFullProfile?.userRegistrationCountry || '',
            screenResolution:
                screen?.width && screen?.height ? `${screen?.width}x${screen?.height}` : '',
            osName:
                getOSName(navigator?.userAgent, {
                    maxTouchPoints: navigator?.maxTouchPoints,
                    platform: navigatorPlatform,
                    userAgentDataPlatform: navigator?.userAgentData?.platform,
                }) || '',
            osLanguage: locale?.split('-')?.[0] || '',
            networkType: navigator?.connection?.effectiveType || '',
        };
    }, [
        locale,
        screen,
        region,
        navigator?.maxTouchPoints,
        navigatorPlatform,
        navigator?.userAgent,
        navigator?.userAgentData?.platform,
        navigator?.connection?.effectiveType,
        userFullProfile?.userRegistrationCountry,
    ]);

    const getPageInfo: DataLayerFunctions['getPageInfo'] = useCallback(
        (pageViewGuid, pageType) => ({
            pageName: location?.pathname,
            pageTitle: documentObject?.title,
            pageType,
            pageUrl: location?.href,
            pageViewGuid,
        }),
        [documentObject?.title, location?.href, location?.pathname],
    );

    const getSiteInfo: DataLayerFunctions['getSiteInfo'] = useCallback(
        ({ optimizelyExp, autoPlayEnabled, genre = OTHER, contentBrand = NA, env, pageType }) => {
            const siteEdition = getSiteEdition(locale, region);
            return {
                brandPlatformId: brandPlatform,
                siteCode: SITE_CODE,
                siteEdition,
                siteHierPrefix: `${SITE_TYPE}:${pageType}`,
                siteId: SITE_ID,
                sitePrimaryRsid: env === 'prod' ? SITE_PRIMARY_RSID : `${SITE_PRIMARY_RSID}-${DEV}`,
                siteTypeDevice,
                optimizelyExp,
                autoPlayEnabled,
                genre,
                contentBrand,
            };
        },
        [locale, region, brandPlatform, siteTypeDevice],
    );

    const tryFireProfilePickerPageLoadBeacon = useCallback(
        (emittedPageType?: string) => {
            // Callers pass the type their snapshot used; everyone else gets the store's current
            // value. Never the render closure: the refs below key the beacon handshake by page
            // type, so a stale read drops the picker page view for the destination.
            const effectivePageType = emittedPageType || getPageType();
            const currentPathname =
                typeof window !== 'undefined' ? window.location.pathname : location?.pathname;
            const syncKey = getSubscriberUtagSyncKey(
                userFullProfile as FullUser,
                effectivePageType,
            );
            if (syncKey && isProfilePickerRoute(effectivePageType, currentPathname)) {
                pickerBeaconAttemptedSyncKeyRef.current = syncKey;
            }

            fireProfilePickerPageLoadBeacon(
                userFullProfile as FullUser,
                effectivePageType,
                lastFiredPageViewSyncKeyRef,
            );
        },
        [userFullProfile, location?.pathname],
    );

    const emitAppBaseSnapshot = useCallback(async (): Promise<AppBaseSnapshotResult> => {
        const currentPageType = getPageType();
        window.adobeDataLayer = window.adobeDataLayer || [];
        const state = ((typeof window.adobeDataLayer.getState === 'function' &&
            window.adobeDataLayer.getState()) ||
            {}) as AdobeDataLayerStateSnapshot;
        const prevPageType = state?.pageInfo?.pageType;
        const resolvedPageType = currentPageType || prevPageType || EMPTY_STR;

        const optimizelyExp = await createOptimizelyExpString(experimentsListForTracking);
        const uuid = generateUUID();
        let userInfo;
        const dataLayerUpdate: Record<string, unknown> = {};

        if (shouldEmitSubscriberTrackingFields(userFullProfile as FullUser, resolvedPageType)) {
            userInfo = getUserInfo(userFullProfile as FullUser);
            Object.assign(dataLayerUpdate, { userInfo });
            const flatUserFields = buildAdobeUserTrackingFields(userFullProfile as FullUser);
            Object.assign(dataLayerUpdate, flatUserFields);
            if (typeof window !== 'undefined' && window.utag_data) {
                Object.assign(window.utag_data, flatUserFields);
            }
            const syncKey = getSubscriberUtagSyncKey(userFullProfile as FullUser, resolvedPageType);
            if (syncKey) {
                lastSyncedSubscriberKeyRef.current = syncKey;
            }
        } else {
            clearSubscriberUtagFields();
            lastSyncedSubscriberKeyRef.current = null;

            const shouldClearAdobeSubscriberState = hasSubscriberAdobeDataLayerState(state);

            if (
                isUserQueryFetched &&
                shouldEmitAnonUserInfo(userFullProfile as FullUser, hasLoggedInSession)
            ) {
                Object.assign(dataLayerUpdate, {
                    userInfo: { userType: USER_TYPE.ANONYMOUS, userStatus: USER_STATUS.SB },
                });
                if (shouldClearAdobeSubscriberState) {
                    Object.assign(dataLayerUpdate, buildClearedSubscriberFlatAdobeFields());
                }
            } else if (shouldClearAdobeSubscriberState) {
                Object.assign(dataLayerUpdate, buildClearedSubscriberAdobeDataLayerFields());
            }
        }

        const domInfo = getDomInfo();
        const siteInfo = getSiteInfo({
            optimizelyExp,
            autoPlayEnabled,
            env,
            pageType: resolvedPageType,
        });
        const pageInfo = getPageInfo(uuid, resolvedPageType);
        const appInfo = getAppInfo();

        Object.assign(dataLayerUpdate, { domInfo, pageInfo, siteInfo, appInfo });

        // Signal to callers whether a fresh snapshot actually landed: gates keyed on
        // INFO_UPDATE must not release against the previous page's retained pageInfo.
        if (isUserDataLoading) return { pushed: false, pageType: resolvedPageType };

        window.adobeDataLayer.push(dataLayerUpdate);
        void TrackerEngine.update({
            type: TrackingType.INFO_UPDATE,
            detail: dataLayerUpdate,
            category: TrackingCategory.App.OPERATION,
        });

        return { pushed: true, pageType: resolvedPageType };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        autoPlayEnabled,
        env,
        getAppInfo,
        getDomInfo,
        getPageInfo,
        getSiteInfo,
        userFullProfile,
        isUserDataLoading,
        isUserQueryFetched,
        hasLoggedInSession,
        experimentsListForTracking,
    ]);

    // Navigation-driven re-emission: ungated; bootstrap is over. useTrackNavigation revokes
    // infoupdate at routeChangeStart; this marks it again — but only once a fresh snapshot
    // for the *current* navigation actually landed (PTVWS-6991):
    //   - if a newer routeChangeStart bumped the generation while the emit was in flight, the
    //     mark is dropped — that navigation's own updateDataLayer owns the re-mark;
    //   - if the user query is mid-flight the push was skipped, so defer the mark until it
    //     settles rather than releasing the gates against the outgoing page's retained pageInfo.
    const emitNavSnapshotAndMarkInfoUpdate = useCallback(async () => {
        const navGeneration = navGenerationRef.current;
        const { pushed, pageType: emittedPageType } = await emitAppBaseSnapshot();
        if (navGeneration !== navGenerationRef.current) return;
        if (!pushed) {
            pendingNavInfoUpdateMarkRef.current = true;
            return;
        }
        BootstrapSignals.mark(BootstrapSignalKeys.INFO_UPDATE);
        // Picker routes defer readDataLayerPageUpdate on routeChangeComplete until
        // subscriber fields are on utag_data (login → home → Who's Watching).
        tryFireProfilePickerPageLoadBeacon(emittedPageType);
    }, [emitAppBaseSnapshot, tryFireProfilePickerPageLoadBeacon]);

    const updateDataLayer = {
        name: UPDATE_DATA_LAYER,
        callback() {
            void emitNavSnapshotAndMarkInfoUpdate();
        },
    };
    useEventListeners(updateDataLayer, [emitNavSnapshotAndMarkInfoUpdate]);

    // Completes a navigation INFO_UPDATE mark that was deferred because the user query was
    // still loading when the nav snapshot was attempted (mirrors the deferred-bootstrap
    // emission below): re-emit with settled user data, then release the gates.
    useEffect(() => {
        if (!pendingNavInfoUpdateMarkRef.current || isUserDataLoading) return;
        pendingNavInfoUpdateMarkRef.current = false;
        void emitNavSnapshotAndMarkInfoUpdate();
    }, [isUserDataLoading, emitNavSnapshotAndMarkInfoUpdate]);

    // Each client navigation opens a new context generation: invalidate any in-flight or
    // deferred nav re-mark from the outgoing page (the incoming updateDataLayer owns it), and
    // allow a fresh picker page-view (dedupe is per-visit only).
    useEffect(() => {
        const onRouteChangeStart = () => {
            if (!window.isMfe) {
                return;
            }

            navGenerationRef.current += 1;
            pendingNavInfoUpdateMarkRef.current = false;
            lastFiredPageViewSyncKeyRef.current = null;
            pickerBeaconAttemptedSyncKeyRef.current = null;
        };

        router.events.on(ROUTE_CHANGE_START, onRouteChangeStart);

        return () => {
            router.events.off(ROUTE_CHANGE_START, onRouteChangeStart);
        };
    }, [router.events]);

    // Initial bootstrap emission: gated on appinit; INFO_UPDATE only when UserFullProfile is ready.
    // Also gated on the live-region siteName being resolved so the one-shot snapshot never emits
    // brandPlatformId from the pinned SSR fallback. When the regional config settles this effect
    // re-runs and schedules the emit with the resolved value.
    useEffect(() => {
        if (
            (hasBootstrappedRef.current && pageType === EMPTY_STR) ||
            isUserDataLoading ||
            !isSiteNameReady
        )
            return;

        const emit = async () => {
            const { pageType: emittedPageType } = await emitAppBaseSnapshot();

            if (!hasBootstrappedRef.current) {
                hasBootstrappedRef.current = true;
                setBootstrapReadyTick((n) => n + 1);
                if (
                    isUserDataReadyForPageView(
                        isUserDataLoading,
                        isUserQueryFetched || isUserQueryError,
                        userFullProfile as FullUser | undefined,
                        isUserQueryError,
                        hasLoggedInSession,
                        pageType,
                    )
                ) {
                    BootstrapSignals.mark(BootstrapSignalKeys.INFO_UPDATE);
                    bootstrapCompletedRef.current = true;
                    tryFireProfilePickerPageLoadBeacon(emittedPageType);
                }
            }
        };

        void BootstrapSignals.whenAll([BootstrapSignalKeys.APP_INIT]).then(emit);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isUserDataLoading, pageType, isSiteNameReady]);

    // UserFullProfile settled after appinit — mark INFO_UPDATE so PAGE_VIEW can proceed.
    useEffect(() => {
        if (!hasBootstrappedRef.current || bootstrapCompletedRef.current) {
            return;
        }

        if (
            !isUserDataReadyForPageView(
                isUserDataLoading,
                isUserQueryFetched || isUserQueryError,
                userFullProfile as FullUser | undefined,
                isUserQueryError,
                hasLoggedInSession,
                pageType,
            )
        ) {
            return;
        }

        void (async () => {
            try {
                const { pageType: emittedPageType } = await emitAppBaseSnapshot();
                BootstrapSignals.mark(BootstrapSignalKeys.INFO_UPDATE);
                bootstrapCompletedRef.current = true;
                tryFireProfilePickerPageLoadBeacon(emittedPageType);
            } catch (e) {
                appLogger.error(
                    'useTrackAppBaseSnapshot deferred bootstrap failed',
                    { component: 'useTrackAppBaseSnapshot' },
                    e instanceof Error ? e : undefined,
                );
            }
        })();
    }, [
        isUserDataLoading,
        isUserQueryFetched,
        isUserQueryError,
        hasLoggedInSession,
        pageType,
        userFullProfile,
        emitAppBaseSnapshot,
        tryFireProfilePickerPageLoadBeacon,
    ]);

    // Fires the trackOptimizelyExpXHR wire beacon, but only for variants the page view
    // could not already have carried.
    const experimentsListFromContext = experimentsContext.state?.experimentsListForTracking;
    useEffect(() => {
        if (!hasBootstrappedRef.current) return;
        if (lastEmittedExperimentsRef.current === experimentsListFromContext) return;
        // SPA nav clears this to a fresh empty object every routeChangeStart; skip scheduling
        // the rAF entirely rather than relying on updateOptimizelyExp's own empty-input guard.
        if (Object.keys(experimentsListForTracking).length === 0) return;
        // isCancelled drops work superseded mid-flight so only the freshest snapshot lands.
        let isCancelled = false;
        let rafId: number | undefined;

        // A held beacon crossing a navigation would stamp the outgoing page's variants onto
        // the destination; bailing without stamping the ref lets that page emit its own.
        const navGeneration = navGenerationRef.current;
        const hasNavigatedAway = () => navGeneration !== navGenerationRef.current;
        const shouldAbandonWrite = () => isCancelled || hasNavigatedAway();

        // `hasBootstrappedRef` opens unconditionally, so without this wait the beacon can
        // precede the page view; only this signal tracks the actual beacon call.
        BootstrapSignals.whenAll([BootstrapSignalKeys.PAGE_VIEW_BEACON_FIRED])
            .then((isReady) => {
                if (isCancelled || hasNavigatedAway()) return;
                if (!isReady) {
                    // Emit anyway on timeout; a late beacon still carries the variants.
                    appLogger.warn(
                        'useTrackAppBaseSnapshot: page-view beacon gate timed out; emitting optimizelyExp beacon late',
                        { component: 'useTrackAppBaseSnapshot' },
                    );
                }
                // The page view emits synchronously inside its own waiter microtask, so a frame's
                // deferral keeps this after it even when both gates release in the same tick.
                rafId = globalThis.requestAnimationFrame(async () => {
                    try {
                        // waitMs: 0 — post-bootstrap XHR path must not stall on pre-consent dataHub.
                        // pushToAdobeDataLayer: true — Adobe Launch reads getState() at beacon time.
                        // markReadyOnSettled: false — bootstrap already marked OPTIMIZELY_EXP_READY.
                        const outcome = await updateOptimizelyExp(experimentsListForTracking, {
                            waitMs: 0,
                            pushToAdobeDataLayer: true,
                            markReadyOnSettled: false,
                            shouldAbandonWrite,
                        });
                        if (isCancelled || hasNavigatedAway()) return;
                        // Only stamp on a write that ran, so a failure can retry on the next settle.
                        if (outcome === undefined) return;
                        lastEmittedExperimentsRef.current = experimentsListFromContext;
                        // Already in the snapshot means the page view carried it; firing would
                        // duplicate.
                        if (!outcome.didChange) return;
                        trackEvent({
                            type: TrackingType.APP_INFO_UPDATE,
                            detail: {
                                context: { siteInfo: { optimizelyExp: outcome.optimizelyExp } },
                            },
                            category: TrackingCategory.App.OPERATION,
                            legacyTrackAction: 'trackOptimizelyExpXHR',
                        });
                    } catch (error) {
                        // updateOptimizelyExp swallows/logs its own async errors; this catch is
                        // only reached by synchronous pre-await failures (e.g., bad input shape).
                        appLogger.error(
                            'useTrackAppBaseSnapshot: failed to schedule optimizelyExp update',
                            { component: 'useTrackAppBaseSnapshot' },
                            error instanceof Error ? error : undefined,
                        );
                    }
                });
            })
            .catch((error: unknown) => {
                appLogger.error(
                    'useTrackAppBaseSnapshot: failed to schedule optimizelyExp beacon',
                    { component: 'useTrackAppBaseSnapshot' },
                    toError(error),
                );
            });

        return () => {
            isCancelled = true;
            if (rafId !== undefined) globalThis.cancelAnimationFrame(rafId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [experimentsListFromContext, bootstrapReadyTick]);

    // After bootstrap, sync subscriber fields onto utag_data and emit the authenticated app base snapshot.
    useEffect(() => {
        if (!bootstrapCompletedRef.current) {
            return;
        }

        // Call-time read, matching `emitAppBaseSnapshot` below: the sync key encodes the page
        // type, so keying it off the render closure would let this run stamp the refs with the
        // previous screen while the snapshot it emits names the new one.
        const currentPageType = getPageType();

        if (
            !isUserDataReadyForPageView(
                isUserDataLoading,
                isUserQueryFetched || isUserQueryError,
                userFullProfile as FullUser | undefined,
                isUserQueryError,
                hasLoggedInSession,
                currentPageType,
            )
        ) {
            return;
        }

        const syncKey = getSubscriberUtagSyncKey(userFullProfile as FullUser, currentPageType);
        if (!syncKey) {
            const hadSyncedSubscriber = lastSyncedSubscriberKeyRef.current != null;
            clearSubscriberUtagFields();
            lastSyncedSubscriberKeyRef.current = null;
            if (hadSyncedSubscriber) {
                emitAppBaseSnapshot();
            }
            return;
        }

        if (lastSyncedSubscriberKeyRef.current === SUBSCRIBER_UTAG_SYNCED_ON_REMOUNT) {
            lastSyncedSubscriberKeyRef.current = syncKey;
            tryFireProfilePickerPageLoadBeacon();
            return;
        }

        if (lastSyncedSubscriberKeyRef.current === syncKey) {
            if (pickerBeaconAttemptedSyncKeyRef.current === syncKey) {
                tryFireProfilePickerPageLoadBeacon();
            }
            return;
        }

        const previousSyncKey = lastSyncedSubscriberKeyRef.current;
        const flatUserFields = buildAdobeUserTrackingFields(userFullProfile as FullUser);
        if (typeof window !== 'undefined' && window.utag_data) {
            Object.assign(window.utag_data, flatUserFields);
        }

        emitAppBaseSnapshot();

        lastSyncedSubscriberKeyRef.current = syncKey;
        if (
            shouldFirePickerBeaconOnSubscriberSyncKeyChange(previousSyncKey, syncKey) ||
            shouldFirePickerBeaconOnPageTypeResolve(previousSyncKey, syncKey)
        ) {
            tryFireProfilePickerPageLoadBeacon();
        }
    }, [
        isUserDataLoading,
        isUserQueryFetched,
        isUserQueryError,
        hasLoggedInSession,
        // Re-run trigger only — the body reads the store.
        pageType,
        userFullProfile?.isLoggedIn,
        userFullProfile?.activeProfile?.id,
        emitAppBaseSnapshot,
        tryFireProfilePickerPageLoadBeacon,
    ]);

    // Retry picker page beacon when Tealium attaches after bootstrap already synced Adobe.
    useEffect(() => {
        const currentPathname =
            typeof window !== 'undefined' ? window.location.pathname : location?.pathname;
        // Call-time read: the retry loop below outlives this render, so a closure value would
        // keep re-firing the beacon for the screen the user has already left.
        const currentPageType = getPageType();
        if (
            !bootstrapCompletedRef.current ||
            !isProfilePickerRoute(currentPageType, currentPathname)
        ) {
            return;
        }

        const syncKey = getSubscriberUtagSyncKey(userFullProfile as FullUser, currentPageType);
        if (!syncKey || lastFiredPageViewSyncKeyRef.current === syncKey) {
            return;
        }

        if (
            lastSyncedSubscriberKeyRef.current !== syncKey ||
            pickerBeaconAttemptedSyncKeyRef.current !== syncKey
        ) {
            return;
        }

        tryFireProfilePickerPageLoadBeacon();

        if (lastFiredPageViewSyncKeyRef.current === syncKey) {
            return;
        }

        const intervalId = window.setInterval(() => {
            tryFireProfilePickerPageLoadBeacon();
            if (lastFiredPageViewSyncKeyRef.current === syncKey) {
                window.clearInterval(intervalId);
            }
        }, PICKER_BEACON_RETRY_INTERVAL_MS);

        const timeoutId = window.setTimeout(() => {
            window.clearInterval(intervalId);
        }, PICKER_BEACON_RETRY_TIMEOUT_MS);

        return () => {
            window.clearInterval(intervalId);
            window.clearTimeout(timeoutId);
        };
    }, [
        // Re-run trigger only — the body reads the store.
        pageType,
        location?.pathname,
        userFullProfile,
        userFullProfile?.isLoggedIn,
        userFullProfile?.activeProfile?.id,
        isUserDataLoading,
        isUserQueryFetched,
        tryFireProfilePickerPageLoadBeacon,
    ]);
};
